/**
 * useUserContext — Single source of truth for user-specific flags.
 *
 * SECURITY: All flags derived from server-verified profile only.
 * Never trust client-side input for access control decisions.
 * Frontend flags are UX only — backend enforces restrictions independently.
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AFRICAN_COUNTRIES = [
  'NG',
  'GH',
  'KE',
  'ZA',
  'UG',
  'TZ',
  'RW',
  'CI',
  'SN',
  'CM',
  'ET',
] as const;

const PAYSTACK_COUNTRIES = ['NG', 'GH', 'KE', 'ZA', 'CI'] as const;

export type Features = {
  momoProtection: boolean;
  networkMonitoring: boolean;
  emailMonitoring: boolean;
  domainMonitoring: boolean;
  deviceMonitoring: boolean;
  cloudMonitoring: boolean;
  marketplace: boolean;
  whatsappAlerts: boolean;
  /** Monitored assets UI — always on; not used for route gating. */
  assetManagement: boolean;
};

export type DisplayContext = {
  entityLabel: string;
  entityLabelCap: string;
  assetLabel: string;
  briefAudience: string;
  alertSubtitle: string;
  breachMessage: string;
};

export type UserContext = {
  isPersonal: boolean;
  isBusiness: boolean;
  isAfrican: boolean;
  isPaystackRegion: boolean;
  features: Features;
  display: DisplayContext;
  authLoading: boolean;
  profileLoaded: boolean;
  countryCode: string;
};

const SAFE_DEFAULT_FEATURES: Features = {
  momoProtection: false,
  networkMonitoring: false,
  emailMonitoring: false,
  domainMonitoring: false,
  deviceMonitoring: false,
  cloudMonitoring: false,
  marketplace: false,
  whatsappAlerts: false,
  assetManagement: true,
};

const BUSINESS_DISPLAY: DisplayContext = {
  entityLabel: 'your business',
  entityLabelCap: 'Your business',
  assetLabel: 'your business',
  briefAudience: 'business',
  alertSubtitle:
    'Every threat we\u2019ve detected for your business \u2014 with clear steps on what to do.',
  breachMessage: 'Your business information has not been found in any data leaks',
};

const PERSONAL_DISPLAY: DisplayContext = {
  entityLabel: 'you',
  entityLabelCap: 'You',
  assetLabel: 'your',
  briefAudience: 'individual',
  alertSubtitle:
    'Every threat we\u2019ve detected for you \u2014 with clear steps on what to do.',
  breachMessage: 'Your information has not been found in any data leaks',
};

export function useUserContext(): UserContext {
  const { userProfile, loading: authLoading } = useAuth();

  return useMemo(() => {
    if (authLoading || !userProfile) {
      return {
        isPersonal: false,
        isBusiness: false,
        isAfrican: false,
        isPaystackRegion: false,
        features: SAFE_DEFAULT_FEATURES,
        display: BUSINESS_DISPLAY,
        authLoading,
        profileLoaded: false,
        countryCode: '',
      };
    }

    const accountType = userProfile.account_type;
    const isPersonal = accountType === 'personal';
    const isBusiness = accountType === 'business';

    const rawCode = userProfile.country_code ?? '';
    const countryCode = /^[A-Z]{2}$/i.test(rawCode.trim())
      ? rawCode.trim().toUpperCase()
      : '';

    const africanList = AFRICAN_COUNTRIES as readonly string[];
    const paystackList = PAYSTACK_COUNTRIES as readonly string[];
    const isAfrican = countryCode !== '' && africanList.includes(countryCode);
    const isPaystackRegion = countryCode !== '' && paystackList.includes(countryCode);

    const features: Features = {
      momoProtection: isAfrican,
      networkMonitoring: true,
      emailMonitoring: true,
      domainMonitoring: true,
      deviceMonitoring: true,
      cloudMonitoring: false,
      marketplace: true,
      whatsappAlerts: isAfrican,
      assetManagement: true,
    };

    const display: DisplayContext = isPersonal ? PERSONAL_DISPLAY : BUSINESS_DISPLAY;

    return {
      isPersonal,
      isBusiness,
      isAfrican,
      isPaystackRegion,
      features,
      display,
      authLoading: false,
      profileLoaded: true,
      countryCode,
    };
  }, [userProfile, authLoading]);
}
